import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { userApi, UserOrganization } from "../api/UserApi";
import { organizationApi } from "../api/OrganizationApi";
import { Period } from "../api/OrganizationSettingsApi";

interface Reporting {
  enabled?: boolean;
  period?: Period;
  recipients?: string[];
}

interface UserSettingsState {
  preferencesLoaded?: boolean;
  organization?: UserOrganization;
  // FIXME
  reporting?: Reporting;
}

const initialState: UserSettingsState = {};

const userSettingsSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    selectOrganization: (
      state,
      {
        payload: { organization },
      }: PayloadAction<{ organization: UserOrganization }>
    ) => {
      state.organization = organization;
    },
    updateOrganizationName: (state, { payload }: PayloadAction<string>) => {
      if (state.organization) {
        state.organization.name = payload;
      }
    },
    updateOrganizationDomain: (state, { payload }: PayloadAction<string>) => {
      if (state.organization) {
        state.organization.emailDomain = payload;
      }
    },
    updateReporting: (state, { payload }: PayloadAction<Reporting>) => {
      state.reporting = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        userApi.endpoints.getUser.matchFulfilled,
        (state, { payload: { admin, preferences } }) => {
          if (admin && admin.organizations.length > 0) {
            const singleOrganization =
              !state.organization && admin.organizations.length === 1;

            const multiOrganizationWithPreference =
              !state.organization &&
              admin.organizations.length > 1 &&
              preferences?.login?.organizationId &&
              preferences?.login?.enabled;

            if (singleOrganization) {
              // Select the first organization on the initial load if only one is present
              [state.organization] = admin.organizations;
            } else if (multiOrganizationWithPreference) {
              // Select the preferred organization from the available organizations
              state.organization = admin.organizations.find(
                (o) => o.publicId === preferences.login?.organizationId
              );
            } else if (state.organization) {
              // Refresh the organization if changed
              const updatedOrganization = admin.organizations.find(
                (organization) =>
                  organization.publicId === state?.organization?.publicId
              );
              // Update the currently selected organization
              if (updatedOrganization) {
                state.organization.name = updatedOrganization.name;
                state.organization.emailDomain =
                  updatedOrganization.emailDomain;
              }
            }
          }
          state.preferencesLoaded = true;
        }
      )
      .addMatcher(
        organizationApi.endpoints.createOrganization.matchFulfilled,
        (state, { payload: { name, emailDomain, publicId } }) => {
          state.organization = {
            name,
            emailDomain,
            publicId,
          };
        }
      );
  },
});

export const {
  selectOrganization,
  updateOrganizationName,
  updateOrganizationDomain,
  updateReporting,
} = userSettingsSlice.actions;

export default userSettingsSlice;
